
import { useState, useEffect } from 'react';

export interface TutorialStep {
  id: string;
  title: string;
  content: string;
  target: string; // CSS selector for the element to highlight
  position: 'top' | 'bottom' | 'left' | 'right';
  allowClickOutside?: boolean;
}

export interface Tutorial {
  id: string;
  name: string;
  steps: TutorialStep[];
}

const TUTORIALS: Tutorial[] = [
  {
    id: 'dashboard-overview',
    name: 'Dashboard Overview',
    steps: [
      {
        id: 'welcome',
        title: 'Welcome to CSO Agent!',
        content: 'Let\'s take a quick tour of your dashboard to help you get started with strategic business planning.',
        target: '[data-tutorial="quick-actions"]',
        position: 'bottom'
      },
      {
        id: 'business-assessment',
        title: 'Start with Business Assessment',
        content: 'Complete your business assessment first. This helps CSO Agent provide personalized advice and better document generation tailored to your specific business needs.',
        target: '[data-tutorial="business-assessment"]',
        position: 'left'
      },
      {
        id: 'quick-actions',
        title: 'Quick Actions',
        content: 'Use these quick actions to create documents, chat with AI, or access key features instantly. These are your main tools for strategic planning.',
        target: '[data-tutorial="quick-actions"]',
        position: 'top'
      },
      {
        id: 'documents',
        title: 'Your Documents',
        content: 'All your strategic documents will appear here. Start creating your business foundation with strategic plans, market research, and more.',
        target: '[data-tutorial="documents"]',
        position: 'top'
      }
    ]
  },
  {
    id: 'document-creation',
    name: 'Creating Your First Document',
    steps: [
      {
        id: 'select-type',
        title: 'Choose Document Type',
        content: 'Select the type of strategic document you want to create. Each template is optimized for specific business needs and your current business stage.',
        target: '[data-tutorial="document-types"]',
        position: 'right'
      },
      {
        id: 'ai-assistance',
        title: 'AI-Powered Generation',
        content: 'Use the "Generate with AI" button to automatically create content based on your business assessment and industry best practices.',
        target: '[data-tutorial="ai-generate"]',
        position: 'left'
      },
      {
        id: 'editing',
        title: 'Edit and Customize',
        content: 'You can edit any generated content directly. The AI learns from your business context to provide relevant suggestions as you work.',
        target: '[data-tutorial="editor"]',
        position: 'top'
      }
    ]
  }
];

export function useTutorial() {
  const [activeTutorial, setActiveTutorial] = useState<Tutorial | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedTutorials, setCompletedTutorials] = useState<string[]>([]);

  useEffect(() => {
    const completed = localStorage.getItem('completed-tutorials');
    if (completed) {
      setCompletedTutorials(JSON.parse(completed));
    }
  }, []);

  const startTutorial = (tutorialId: string) => {
    console.log('Starting tutorial:', tutorialId);
    const tutorial = TUTORIALS.find(t => t.id === tutorialId);
    if (tutorial) {
      setActiveTutorial(tutorial);
      setCurrentStepIndex(0);
      console.log('Tutorial started:', tutorial);
    } else {
      console.error('Tutorial not found:', tutorialId);
    }
  };

  const nextStep = () => {
    if (activeTutorial && currentStepIndex < activeTutorial.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      completeTutorial();
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const completeTutorial = () => {
    if (activeTutorial) {
      const newCompleted = [...completedTutorials, activeTutorial.id];
      setCompletedTutorials(newCompleted);
      localStorage.setItem('completed-tutorials', JSON.stringify(newCompleted));
      
      // Mark welcome tutorial as seen when dashboard overview is completed
      if (activeTutorial.id === 'dashboard-overview') {
        localStorage.setItem('has-seen-welcome-tutorial', 'true');
      }
    }
    setActiveTutorial(null);
    setCurrentStepIndex(0);
    // No completion notification shown
  };

  const skipTutorial = () => {
    completeTutorial();
  };

  const forceDismiss = () => {
    setActiveTutorial(null);
    setCurrentStepIndex(0);
  };

  const currentStep = activeTutorial?.steps[currentStepIndex];

  return {
    activeTutorial,
    currentStep,
    currentStepIndex,
    completedTutorials,
    startTutorial,
    nextStep,
    prevStep,
    completeTutorial,
    skipTutorial,
    forceDismiss,
    isFirstStep: currentStepIndex === 0,
    isLastStep: activeTutorial ? currentStepIndex === activeTutorial.steps.length - 1 : false,
    availableTutorials: TUTORIALS
  };
}
